import {
  forwardRef,
  useEffect,
  type KeyboardEvent,
  type SyntheticEvent
} from 'react'

import { motion, type HTMLMotionProps } from 'motion/react'

import { cn } from '../../../utils/cn'
import { Icon } from '../icon/icon'

import { hasOpenPopup, useButtonMotion } from './button-motion'
import { ButtonRipple } from './button-ripple'
import {
  ButtonTrailingIcon,
  type ButtonTrailingIconOptions
} from './button-trailing-icon'
import {
  DEFAULT_SIZE,
  DEFAULT_VARIANT,
  resolveSlots,
  type ButtonSize,
  type ButtonVariantOptions,
  type LegacyButtonSize
} from './button-variants'

export { buttonClassName } from './button-variants'
export type { ButtonSize, ButtonVariant } from './button-variants'

const warnedLegacySizes = new Set<LegacyButtonSize>()

const preventLoadingInteraction = (
  event: SyntheticEvent<HTMLButtonElement>
) => {
  event.preventDefault()
  event.stopPropagation()
}

const preventLoadingKeyActivation = (
  event: KeyboardEvent<HTMLButtonElement>
) => {
  // Keep focus navigation and dismissal available while blocking trigger keys.
  if (
    event.key === 'Enter' ||
    event.key === ' ' ||
    event.key === 'ArrowDown' ||
    event.key === 'ArrowUp'
  ) {
    preventLoadingInteraction(event)
  }
}

const DOT_ANIMATE = { y: ['0em', '-0.4em', '0em'] }
const DOT_TRANSITIONS = [0, 1, 2].map(index => ({
  duration: 0.6,
  ease: 'easeInOut' as const,
  repeat: Infinity,
  repeatDelay: 0.2,
  delay: index * 0.1
}))

/**
 * Motion — shared with ButtonLink through `./button-motion` — all of it behind
 * `animated`, and skipped for reduced-motion users and for disabled/loading
 * buttons: a 1.02 hover lift and a 0.97 press scale riding one spring, plus
 * the Material press ripple in `./button-ripple`. As a Base UI trigger the
 * button drops the lift while its popup is open (reading the same
 * `data-popup-open` the caret does): Base UI positions the popup from the
 * trigger's transformed bounding box, so a lift under an open menu makes it
 * jitter. Press and ripple stay.
 * Loading trades the label for jumping dots and keeps the live palette on
 * purpose: `data-disabled` marks explicit disablement, while `data-loading`
 * keeps the busy state distinct. Grey-out rules require disabled without loading.
 *
 * `type` defaults to `'button'`. HTML defaults it to `'submit'` instead, which
 * means a button that only meant to open a dialog or toggle a row silently
 * submits the nearest form — and the failure is invisible in review, because
 * nothing in the call site mentions the form it breaks. The button this one
 * replaced across the app, `lib/ui/Button`, carried the same default; leaving
 * it off produced four separate production-shaped bugs during that migration
 * (an autocomplete that also saved the profile, two Cancel buttons that
 * submitted instead of closing, and a "Select all" that saved and dismissed the
 * drawer it lived in). A component the whole app reaches for should not make
 * every caller remember a workaround, so the safe value is the default and the
 * submitting button is the one that declares itself.
 */
export type ButtonProps = Omit<HTMLMotionProps<'button'>, 'size'> &
  ButtonVariantOptions &
  ButtonTrailingIconOptions & {
    /**
     * `'xs'` and `'icon'` are deprecated: Ømni has three sizes. Both still
     * render their pre-Ømni geometry unchanged, so nothing shifts under you —
     * migrate to `'sm'` and to `iconOnly` respectively when you next touch the
     * screen.
     */
    size?: ButtonSize | LegacyButtonSize
    /**
     * Renders a square button holding a single icon (48/40/32). Needs an
     * `aria-label` — there is no text to name the control.
     */
    iconOnly?: boolean
    /**
     * Appends a decorative caret without a divider. It flips with
     * `aria-expanded` or Base UI's `data-popup-open`. Ignored alongside
     * `iconOnly`; `caretWithDivider` supplies its own caret when enabled.
     */
    caret?: boolean
    /** Preserves consumer-specific caret styling, such as a muted field icon. */
    caretClassName?: string
    /** Classes for the content layout; avoids targeting internal slot selectors. */
    contentClassName?: string
    /**
     * Appends a decorative divider and caret within the same click target. The caret flips when the
     * button carries `aria-expanded` or Base UI's `data-popup-open`. Ignored
     * alongside `iconOnly`: that box is a fixed square with no room for a second
     * affordance, so rendering both would overflow it.
     */
    caretWithDivider?: boolean
    /**
     * Set to `false` to drop the hover lift, press scale and ripple.
     * Reduced-motion users and disabled/loading buttons opt out on their own.
     * Defaults to `true`.
     */
    animated?: boolean
    /**
     * Swaps the label for jumping dots and blocks interaction. Width stays
     * stable and focus is retained unless `disabled` is also explicitly set.
     */
    loading?: boolean
    /**
     * Defaults to `'button'`, so a button inside a form does nothing to that
     * form unless it asks to. Pass `'submit'` on the one that submits.
     */
    type?: 'button' | 'submit' | 'reset'
  }

/**
 * Three dots taking turns hopping. Sized in `em` so they track the label: at
 * 16/14/12px text the dots are 6/5.25/4.5px and travel 6.4/5.6/4.8px.
 */
const ButtonLoadingDots = ({ animated }: { animated: boolean }) => (
  <>
    {DOT_TRANSITIONS.map((transition, index) => (
      <motion.span
        key={index}
        className='size-[0.375em] rounded-full bg-current'
        animate={animated ? DOT_ANIMATE : undefined}
        transition={animated ? transition : undefined}
      />
    ))}
  </>
)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = DEFAULT_VARIANT,
      size = DEFAULT_SIZE,
      iconOnly = false,
      caretWithDivider = false,
      caret = false,
      caretClassName,
      trailingIcon,
      trailingIconMotion,
      className,
      contentClassName,
      disabled,
      loading = false,
      animated = true,
      type = 'button',
      children,
      onClick,
      onPointerDown,
      ...props
    },
    ref
  ) => {
    useEffect(() => {
      if (
        process.env.NODE_ENV !== 'development' ||
        (size !== 'xs' && size !== 'icon') ||
        warnedLegacySizes.has(size)
      ) {
        return
      }

      warnedLegacySizes.add(size)
      const replacement =
        size === 'icon'
          ? 'Use iconOnly with sm, md, or lg and an aria-label.'
          : 'Use sm, md, or lg.'
      // eslint-disable-next-line no-console -- Development-only migration guidance, once per legacy size.
      console.warn(
        `[acqua/Button] size="${size}" is deprecated. ${replacement} Review the layout before migrating: legacy dimensions are preserved and the new sizes differ.`
      )
    }, [size])

    const isDisabled = disabled || loading
    const {
      reducedMotion,
      shouldAnimate,
      ripple,
      initialRipples,
      handlePointerDown,
      motionProps
    } = useButtonMotion<HTMLButtonElement>({
      animated,
      disabled: isDisabled,
      lift: !hasOpenPopup(props as Record<string, unknown>),
      onPointerDown
    })

    const slots = resolveSlots(variant, size, iconOnly)

    return (
      <motion.button
        ref={ref}
        data-slot='button'
        data-variant={variant}
        data-size={size}
        data-icon-only={iconOnly || undefined}
        data-motion-enabled={shouldAnimate || undefined}
        data-loading={loading ? 'true' : undefined}
        data-disabled={disabled ? 'true' : undefined}
        className={cn(slots.root, className)}
        type={type}
        disabled={disabled}
        {...motionProps}
        {...props}
        aria-busy={loading || props['aria-busy']}
        aria-disabled={loading || props['aria-disabled']}
        onClick={loading ? preventLoadingInteraction : onClick}
        onPointerDown={loading ? preventLoadingInteraction : handlePointerDown}
        onMouseDown={loading ? preventLoadingInteraction : props.onMouseDown}
        onKeyDown={loading ? preventLoadingKeyActivation : props.onKeyDown}
        onKeyUp={loading ? preventLoadingKeyActivation : props.onKeyUp}
      >
        {initialRipples && (
          <ButtonRipple
            ref={ripple}
            initialRipples={initialRipples}
            enabled={shouldAnimate}
          />
        )}
        <span
          data-slot='button-content'
          className={cn(
            slots.content,
            contentClassName,
            loading && 'opacity-0'
          )}
        >
          {children as React.ReactNode}
          {trailingIcon && !iconOnly && (
            <ButtonTrailingIcon
              name={trailingIcon}
              motion={trailingIconMotion}
            />
          )}
          {caret && !caretWithDivider && !iconOnly && (
            <Icon
              name='keyboard_arrow_down'
              data-slot='button-caret'
              aria-hidden
              className={cn(slots.caret, caretClassName)}
            />
          )}
        </span>
        {caretWithDivider && !iconOnly && (
          <span
            data-slot='button-actions'
            aria-hidden='true'
            className={loading ? `${slots.actions} opacity-0` : slots.actions}
          >
            <span data-slot='button-divider' className={slots.divider} />
            <Icon name='keyboard_arrow_down' className={slots.caret} />
          </span>
        )}
        {loading && (
          <span
            data-slot='button-spinner'
            aria-hidden='true'
            className={slots.spinner}
          >
            <ButtonLoadingDots animated={!reducedMotion} />
          </span>
        )}
      </motion.button>
    )
  }
)
Button.displayName = 'Button'
